<template>
  <v-row no-gutters class="sz-primary-grey--text pa-5">
    <v-col cols="12">
      <v-row no-gutters justify="space-between">
        <v-col cols="12" sm="6" order="last" order-sm="first">
          <h2 class="font-weight-medium f-20 mb-5">
            {{ $t('label.warehouse') }}
          </h2>
          <p class="text-h3 font-weight-medium border-bottom-red mb-3">
            {{ warehouse.name }}
          </p>
        </v-col>
        <v-col
          v-if="isSupporting"
          cols="12"
          sm="auto"
          order="first"
          order-sm="last"
          class="hidden-print-only"
        >
          <p class="py-1 px-3 text-right sz-light-background d-inline-block sz-white--text f-14">
            {{ $t('family.warehouse.supportFor') }}
          </p>
          <v-tooltip color="#ffffff" location="top" max-width="300">
            <template #activator="{ props }">
              <v-icon v-bind="props" class="ml-3 sz-primary-violet--text" :size="16">
                $information
              </v-icon>
            </template>
            <p class="mb-0 py-3 sz-primary-grey--text f-12">
              {{ $t('family.warehouse.tooltip') }}
            </p>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12">
      <v-row>
        <v-col cols="12" sm="6">
          <v-row no-gutters>
            <v-col cols="12">
              <p class="font-weight-bold f-20">
                {{ $t('family.warehouse.where') }}
              </p>
            </v-col>
            <v-col cols="12" sm="6" class="pr-sm-1">
              <p class="my-sm-3 font-weight-medium f-20">
                {{ $t('family.warehouse.address') }}
              </p>
              <p class="f-16 font-weight-regular mb-2">
                {{ warehouse.locationName }}
              </p>
              <p class="f-16 font-weight-regular mb-0">
                {{ fullStreet }}
              </p>
              <p class="f-16 font-weight-regular">
                {{ filterPostalCode(warehouse.postalCode) }} {{ warehouse.city }}
              </p>
            </v-col>
            <v-col cols="12" sm="6">
              <p class="my-sm-3 font-weight-medium f-20">
                {{ $t('family.warehouse.opening') }}
              </p>
              <p
                v-if="additionalFirstDayChecked || isSupporting"
                class="f-16 font-weight-regular mb-0"
              >
                {{ days.first }}: {{ filterHours(openingHours.firstDayFrom) }}-{{
                  filterHours(openingHours.firstDayTo)
                }}
              </p>
              <p class="f-16 font-weight-regular mb-0">
                {{ days.second }}: {{ filterHours(openingHours.secondDayFrom) }}-{{
                  filterHours(openingHours.secondDayTo)
                }}
              </p>
              <p
                v-if="additionalThirdDayChecked && !isSupporting"
                class="f-16 font-weight-regular mb-0"
              >
                {{ days.third }}: {{ filterHours(openingHours.thirdDayFrom) }}-{{
                  filterHours(openingHours.thirdDayTo)
                }}
              </p>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" sm="6" v-if="isSupporting && warehouseNative" class="needs-border-left">
          <v-row no-gutters>
            <v-col cols="12">
              <p class="font-weight-bold f-20">
                {{ $t('family.warehouse.packageDestinationLabel') }}
              </p>
            </v-col>
            <v-col cols="12" sm="6" class="pr-sm-1">
              <p class="my-sm-3 f-20 font-weight-regular">
                {{ $t('family.warehouse.packageDestinationInfo') }}
              </p>
            </v-col>
            <v-col cols="12" sm="6">
              <p class="mt-sm-3 f-20 sz-primary-grey--text font-weight-medium">
                {{ warehouseNative.name }}
              </p>
              <p class="f-16 sz-primary-grey--text mb-0">
                {{ $t('family.warehouse.provinceShort') }}
                {{ warehouseNative.province }}
              </p>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { computed } from 'vue'
import { filterHours } from '@/utils/filterTime'
import { filterPostalCode } from '@/utils/filterPostalCode'

const props = defineProps({
  warehouse: {
    required: true
  },
  warehouseNative: {
    required: false
  }
})

const fullStreet = computed(() => {
  const _flat = props.warehouse.flatNumber !== null ? '/' + props.warehouse.flatNumber : ''
  const _street = props.warehouse.street ? props.warehouse.street : ''
  return _street + ' ' + props.warehouse.buildingNumber + _flat
})

const openingHours = computed(() => {
  return props.warehouse.warehouseData && props.warehouse.warehouseData.location_data.opening_hours
})

const additionalFirstDayChecked = computed(() => {
  return openingHours.value?.additionalFirstDayChecked === 'yes'
})

const additionalThirdDayChecked = computed(() => {
  return openingHours.value?.additionalThirdDayChecked === 'yes'
})

const days = computed(() => {
  return {
    first: (props.warehouse.sheetData && props.warehouse.sheetData[0].description) || '',
    second: (props.warehouse.sheetData && props.warehouse.sheetData[1].description) || '',
    third: (props.warehouse.sheetData && props.warehouse.sheetData[2].description) || ''
  }
})

const isSupporting = computed(() => {
  return props.warehouse.type === 'supporting'
})
</script>
